/**
 * Error taxonomy — 13 concrete classes, all derived from {@link CryptoError}.
 *
 * Every error carries a stable `code` string and a `kind: 'crypto'` tag so
 * logging filters can redact crypto failures without leaking inputs. Messages
 * MUST never include plaintext, passphrase length, key bytes, or ciphertext —
 * only the class name + code. The CI canary-plaintext grep watches for leaks.
 */
export class CryptoError extends Error {
    kind = 'crypto';
    constructor(message) {
        super(message);
        // Restore prototype chain across transpilation targets.
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = new.target.name;
    }
}
/** A sodium consumer was called before `initSodium()` resolved. */
export class SodiumNotInitialisedError extends CryptoError {
    code = 'E_SODIUM_NOT_READY';
    constructor() {
        super('libsodium not initialised — call initSodium() and await it first');
    }
}
/** blob[0] is not the expected version byte. Rejected before any ciphertext read. */
export class VersionByteRejectedError extends CryptoError {
    code = 'E_VERSION';
    constructor() {
        super('unsupported version byte');
    }
}
/** blob[1] is not the expected KindByte for this call site. */
export class KindByteRejectedError extends CryptoError {
    code = 'E_KIND';
    constructor() {
        super('unexpected kind byte');
    }
}
/** XChaCha20-Poly1305 tag failure: wrong KEK, tampered blob, or wrong AAD. */
export class EnvelopeAuthTagMismatchError extends CryptoError {
    code = 'E_ENVELOPE_AUTH';
    constructor() {
        super('envelope authentication tag mismatch');
    }
}
/** AES-256-GCM tag failure on an item payload. */
export class ItemAuthTagMismatchError extends CryptoError {
    code = 'E_ITEM_AUTH';
    constructor() {
        super('item authentication tag mismatch');
    }
}
/** AES-256-GCM tag failure on a wrapped DEK. */
export class DekUnwrapError extends CryptoError {
    code = 'E_DEK_UNWRAP';
    constructor() {
        super('DEK unwrap failed');
    }
}
/** Argon2id params are not exactly V1 or FALLBACK_256 (blocks silent downgrade). */
export class Argon2idParamError extends CryptoError {
    code = 'E_ARGON_PARAMS';
    constructor() {
        super('Argon2id params not in the allow-list (V1 or FALLBACK_256)');
    }
}
/** 24-word input fails the BIP-39 checksum. */
export class RecoveryCodeChecksumError extends CryptoError {
    code = 'E_BIP39_CHECKSUM';
    constructor() {
        super('recovery code checksum invalid');
    }
}
/** A submitted word is not in the English BIP-39 wordlist. */
export class RecoveryCodeWordError extends CryptoError {
    code = 'E_BIP39_WORD';
    constructor() {
        super('recovery code contains an unknown word');
    }
}
/** The recovery code does not have exactly 24 words. */
export class RecoveryCodeLengthError extends CryptoError {
    code = 'E_BIP39_LENGTH';
    constructor() {
        super('recovery code must be exactly 24 words');
    }
}
/** The bundled wordlist SHA-256 does not match the canonical fingerprint. */
export class WordlistIntegrityError extends CryptoError {
    code = 'E_BIP39_WORDLIST';
    constructor() {
        super('BIP-39 wordlist integrity check failed');
    }
}
/** Defensive tripwire: someone tried to JSON.stringify a DEK or UMK. */
export class DekSerializationError extends CryptoError {
    code = 'E_DEK_SERIALIZE';
    constructor() {
        super('refusing to serialise key material');
    }
}
/** `crypto.getRandomValues` is unavailable (non-browser, non-node host). */
export class RngUnavailableError extends CryptoError {
    code = 'E_RNG';
    constructor() {
        super('no CSPRNG available — refusing to proceed');
    }
}
/** HKDF deriveBits failed (e.g. malformed UMK length) during UMK-proof. */
export class UmkProofKeyDerivationError extends CryptoError {
    code = 'E_UMKPROOF_HKDF';
    constructor() {
        super('UMK-proof key derivation failed');
    }
}
/** X25519 key agreement failed — degenerate/low-order or substituted peer key (device pairing §12.2/§12.4). */
export class DeviceKeyAgreementError extends CryptoError {
    code = 'E_DEVICE_KEX';
    constructor() {
        super('device key agreement failed');
    }
}
//# sourceMappingURL=errors.js.map